import { Avatar, Box, Button, CircularProgress, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { FiCpu, FiMessageSquare, FiPhone, FiUser } from 'react-icons/fi';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  isLoading?: boolean;
  showContactButtons?: boolean;
}

interface ChatMessageProps {
  message: Message;
  onPreferCall?: () => void;
  onPreferText?: () => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onPreferCall, onPreferText }) => {
  const isUser = message.sender === 'user';
  const showInitialLoading = message.isLoading && message.text.length === 0 && !isUser;

  return (
    <VStack w="100%" alignItems={isUser ? 'flex-end' : 'flex-start'} px={{ base: 1, md: 2 }} spacing={1}>
      <HStack
        spacing={isUser ? 0 : { base: 1, md: 3 }}
        maxWidth={{ base: '95%', md: '80%' }}
        alignItems="flex-start"
        flexDirection={isUser ? 'row-reverse' : 'row'}
      >
        <Avatar
          size={{ base: 'xs', md: 'sm' }}
          icon={<Icon as={isUser ? FiUser : FiCpu} fontSize={{ base: '0.8rem', md: '1.2rem' }} />}
          bg={isUser ? 'cyan.500' : 'purple.500'}
          color="white"
          ml={isUser ? { base: 1, md: 3 } : 0}
          mr={!isUser ? { base: 1, md: 0 } : 0}
        />
        <Box
          bg={isUser ? 'cyan.500' : showInitialLoading ? 'transparent' : 'gray.100'}
          color={isUser ? 'white' : 'black'}
          px={showInitialLoading ? 0 : { base: 2, md: 4 }}
          py={showInitialLoading ? 2 : { base: 2, md: 3 }}
          borderRadius="lg"
          borderTopLeftRadius={isUser ? 'lg' : showInitialLoading ? 'lg' : '0'}
          borderTopRightRadius={isUser ? '0' : showInitialLoading ? 'lg' : 'lg'}
        >
          {showInitialLoading ? (
            <Flex alignItems="center" justifyContent="center" h="100%">
              <CircularProgress isIndeterminate size="20px" color="teal.500" mr={2} />
              <Text fontSize="md" color="gray.500">
                Thinking...
              </Text>
            </Flex>
          ) : (
            <Text whiteSpace="pre-wrap" fontSize="md">
              {message.text}
              {message.isLoading && !isUser && <CircularProgress isIndeterminate size="12px" ml={2} />}
            </Text>
          )}
        </Box>
      </HStack>
      {message.sender === 'ai' && message.showContactButtons && (
        <Box pt={1} pl={isUser ? 0 : { base: '28px', md: '44px' }} w="full" maxWidth={{ base: '95%', md: '80%' }}>
          <Text mb={1} fontSize="xs" color="gray.600">
            An advisor can help with that. How would you like to connect?
          </Text>
          <HStack spacing={2}>
            <Button
              size="sm"
              leftIcon={<Icon as={FiPhone} />}
              colorScheme="purple"
              variant="outline"
              onClick={onPreferCall}
            >
              Prefer a Call
            </Button>
            <Button
              size="sm"
              leftIcon={<Icon as={FiMessageSquare} />}
              colorScheme="purple"
              variant="outline"
              onClick={onPreferText}
            >
              Prefer a Text
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

export default ChatMessage;
